import React, { useEffect } from 'react';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import ZoomLogo from '../../../images/zoom-logo.svg';
import MicrosoftTeamsLogo from '../../../images/microsoft-teams-logo.svg';
import WebexLogo from '../../../images/webex-logo.svg';
import { Pages } from '../setup-conference-room';
import { ConferenceRoomEntryContainer, SIPAddressDisplay, CodeBox, PlatformButton } from './elements';

interface Props {
    setPage: (page: number) => void;
    setSIPType: (sipType: string) => void;
    setEntryCode: (code: string) => void;
    initialCode?: string;
}

export default function ConferenceRoomEntry({
	setPage,
	setSIPType,
	setEntryCode,
	initialCode,
}: Props) {
	useEffect(() => {
		if (initialCode) {
			const codeArray = initialCode.split('');
			for (let i = 0; i < 5; i += 1) {
				const element = document.getElementById(`passcode-input-${i}`) as HTMLInputElement;
				if (element) {
					element.value = codeArray[i] || '';
				}
			}
		}
	}, [initialCode]);
    const environment = process.env.REACT_APP_NODE_ENV;
    const sipAddress =  `o.brandlive${environment === 'production' ? '' : `-${environment?.toLowerCase()}`}@joingw.com`;

	const onPlatformClick = (platform: 'zoom' | 'teams' | 'webex' | 'google-meet' | 'hp-poly' | 'other') => {
		if (platform === 'zoom' || platform === 'teams' || platform === 'webex' || platform === 'other') {
			setSIPType(platform);
			setPage(Pages.SIPConfig);
		}
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>, digit: number) => {
		e.target.value = e.target.value.toUpperCase();
		if (e.target.value.length > 0 && digit < 4) {
			document.getElementById(`passcode-input-${digit + 1}`)?.focus();
		}
	};

	const handleSubmit = async () => {
		const code = [
			(document.getElementById('passcode-input-0') as HTMLInputElement).value,
			(document.getElementById('passcode-input-1') as HTMLInputElement).value,
			(document.getElementById('passcode-input-2') as HTMLInputElement).value,
			(document.getElementById('passcode-input-3') as HTMLInputElement).value,
			(document.getElementById('passcode-input-4') as HTMLInputElement).value,
		].join('');

		setEntryCode(code);
		setPage(Pages.ViewConfig);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, digit: number) => {
		if (e.key === 'Delete') {
			if (e.currentTarget.value) {
				e.currentTarget.value = '';
			} else if (digit < 4) {
				const element = document.getElementById(`passcode-input-${digit + 1}`) as HTMLInputElement;
				element.value = '';
				element.focus();
			}
		} else if (e.key === 'Backspace') {
			if (e.currentTarget.value) {
				e.currentTarget.value = '';
			} else if (digit > 0) {
				const element = document.getElementById(`passcode-input-${digit - 1}`) as HTMLInputElement;
				element.value = '';
				element.focus();
			}
		} else if (e.key === 'Enter') {
			handleSubmit();
		} else if (e.key === 'ArrowRight' && digit < 4) {
			document.getElementById(`passcode-input-${digit + 1}`)?.focus();
		} else if (e.key === 'ArrowLeft' && digit > 0) {
			document.getElementById(`passcode-input-${digit - 1}`)?.focus();
		} else if (e.key.length > 1 || !e.key.match(/[A-Z0-9a-z]/) || !e.currentTarget.value) {
			// do not overwrite the value if the key is not a letter or number
		} else {
			e.currentTarget.value = e.key.toUpperCase();
			if (digit < 4) {
				setTimeout(() => {
					document.getElementById(`passcode-input-${digit + 1}`)?.focus();
				}, 0);
			}
		}
	};

	const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const paste = e.clipboardData.getData('text').toUpperCase().split('').filter((c) => c.match(/[A-Z0-9]/));
		for (let i = 0; i < 5; i += 1) {
			const value = paste[i] || '';
			const element = document.getElementById(`passcode-input-${i}`) as HTMLInputElement;
			if (element) {
				element.value = value;
			}
		}
	};

	return (
        <ConferenceRoomEntryContainer>
            <div className="conference-room-header">
                <h1>Greenroom for<br/>Conference rooms</h1>
            </div>

            <div className="conference-room-content">
                <div className="sip-section">
                    <p className="section-label">
                        Enter the following SIP address into your conferencing unit
                    </p>
                    <SIPAddressDisplay>
                        {sipAddress}
                    </SIPAddressDisplay>
                </div>

                <div className="code-section">
                    <p className="section-label">
                        Then, enter the code displayed on your TV
                    </p>
                    <div className="code-inputs">
                        <CodeBox
                            id="passcode-input-0"
                            maxLength={1}
                            onChange={(e) => onChange(e, 0)}
                            onKeyDown={(e) => onKeyDown(e, 0)}
                            onPaste={onPaste}
                        />
                        <CodeBox
                            id="passcode-input-1"
                            maxLength={1}
                            onChange={(e) => onChange(e, 1)}
                            onKeyDown={(e) => onKeyDown(e, 1)}
                            onPaste={onPaste}
                        />
                        <CodeBox
                            id="passcode-input-2"
                            maxLength={1}
                            onChange={(e) => onChange(e, 2)}
                            onKeyDown={(e) => onKeyDown(e, 2)}
                            onPaste={onPaste}
                        />
                        <CodeBox
                            id="passcode-input-3"
                            maxLength={1}
                            onChange={(e) => onChange(e, 3)}
                            onKeyDown={(e) => onKeyDown(e, 3)}
                            onPaste={onPaste}
                        />
                        <CodeBox
                            id="passcode-input-4"
                            maxLength={1}
                            onChange={(e) => {
                                onChange(e, 4); 
                                handleSubmit();
                            }}
                            onKeyDown={(e) => onKeyDown(e, 4)}
                            onPaste={onPaste}
                        />
                    </div>
					<p className="section-label" style={{ textDecoration: 'underline', cursor: 'pointer', marginTop: '20px' }} onClick={() => setPage(Pages.SIPConfig)}>
                        Use Legacy SIP Join Flow
                    </p>
                </div>
            </div>

            
        </ConferenceRoomEntryContainer>
	);
}
